/**
 * WatchlistPage — Sprint 19 Personal Intelligence Layer.
 *
 * Surfaces the user's personally curated book (user_watchlist) with:
 *   - Tier segmentation: Core (held), Active (traded), Monitor (watch)
 *   - Per-ticker news feed (Finnhub + classification)
 *   - Polymarket divergence panel
 *   - KAIROS view on user tickers (anchored recommendations)
 *   - Legacy sector hierarchy preserved underneath
 *
 * Reuses AssetCard / AssetDetailModal / AddAssetButton per P13.
 */

import { useState, useEffect } from 'react';
import { ListChecks, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronRight, Zap, ExternalLink, Newspaper, Target, AlertTriangle } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';
import AssetCard from '../components/AssetCard.jsx';
import AssetDetailModal from '../components/AssetDetailModal.jsx';
import AddAssetButton from '../components/AddAssetButton.jsx';

const STANCE_COLORS = { bullish: '#10b981', bearish: '#ef4444', neutral: '#6b7280' };
const SIGNAL_COLORS = { buy: '#10b981', sell: '#ef4444', hold: '#f59e0b' };
const STANCE_ICONS = { bullish: TrendingUp, bearish: TrendingDown, neutral: Minus };
const TIER_COPY = {
  core: { label: 'Core holdings', sub: 'Long-term conviction — the book you actually own.' },
  active: { label: 'Active tier', sub: 'Tickers you trade around. KAIROS will tailor short/medium-horizon recs here.' },
  monitor: { label: 'Monitor', sub: 'Watched for thesis fit or opportunistic entries. No per-ticker news cost.' },
};

// Sprint 21.7: theme-grouped view — tickers appear in each theme they belong to
function ThemeGroupedView({ rows, taxonomy, expanded, setExpanded, onClick, onRefresh }) {
  // Build theme → tickers map from asset_taxonomy
  const themeMap = {};
  for (const r of rows) {
    const tax = taxonomy[r.ticker];
    const themes = (tax?.themes && tax.themes.length > 0) ? tax.themes : ['Unclassified'];
    for (const t of themes) {
      if (!themeMap[t]) themeMap[t] = [];
      themeMap[t].push(r);
    }
  }
  const themeNames = Object.keys(themeMap).sort((a, b) => themeMap[b].length - themeMap[a].length);

  if (themeNames.length === 0) {
    return <div className="text-[10px] text-korinth-muted font-mono text-center py-4 italic">No classified tickers yet. Visit /settings to refresh taxonomy.</div>;
  }

  return (
    <div className="space-y-2">
      {themeNames.map(themeName => {
        const tickers = themeMap[themeName];
        const isOpen = expanded[themeName] !== false; // default open
        // sort by tier priority: core > active > monitor
        const tierPri = { core: 0, active: 1, monitor: 2 };
        const sorted = [...tickers].sort((a, b) => (tierPri[a.tier] ?? 3) - (tierPri[b.tier] ?? 3) || a.ticker.localeCompare(b.ticker));
        return (
          <div key={themeName} className="bg-korinth-surface border border-korinth-border rounded-lg">
            <button onClick={() => setExpanded(e => ({ ...e, [themeName]: !isOpen }))}
              className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-korinth-bg/50 rounded-t-lg">
              {isOpen ? <ChevronDown className="w-3 h-3 text-korinth-muted" /> : <ChevronRight className="w-3 h-3 text-korinth-muted" />}
              <span className="text-xs font-mono font-bold text-white">{themeName}</span>
              <span className="text-[10px] text-korinth-muted">({tickers.length})</span>
            </button>
            {isOpen && (
              <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {sorted.map(r => (
                  <AssetCard
                    key={`${themeName}::${r.ticker}`}
                    symbol={r.ticker} name={r.name} category={r.sector} tier={r.tier}
                    asset_type={r.asset_type || 'stock'}
                    price_usd={r.latest_price} change_24h={r.change_24h}
                    kairos_view={r.kairos_view || null}
                    themes={taxonomy[r.ticker]?.themes || []}
                    catalysts={r.recent_news || []}
                    your_position={r.holding_pct != null ? { holding_pct: r.holding_pct, target: null, note: r.personal_notes } : null}
                    onClick={() => onClick(r.ticker)}
                    show_tier_controls={true} on_tier_changed={onRefresh} on_removed={onRefresh}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Sprint 21.7: GICS sector-grouped view
function SectorGroupedView({ rows, taxonomy, expanded, setExpanded, onClick, onRefresh }) {
  const sectorMap = {};
  for (const r of rows) {
    const sec = taxonomy[r.ticker]?.gics_sector || 'Unclassified';
    if (!sectorMap[sec]) sectorMap[sec] = [];
    sectorMap[sec].push(r);
  }
  const sectorNames = Object.keys(sectorMap).sort((a, b) => sectorMap[b].length - sectorMap[a].length);
  return (
    <div className="space-y-2">
      {sectorNames.map(secName => {
        const tickers = sectorMap[secName];
        const isOpen = expanded[`sec::${secName}`] !== false;
        return (
          <div key={secName} className="bg-korinth-surface border border-korinth-border rounded-lg">
            <button onClick={() => setExpanded(e => ({ ...e, [`sec::${secName}`]: !isOpen }))}
              className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-korinth-bg/50 rounded-t-lg">
              {isOpen ? <ChevronDown className="w-3 h-3 text-korinth-muted" /> : <ChevronRight className="w-3 h-3 text-korinth-muted" />}
              <span className="text-xs font-mono font-bold text-white">{secName}</span>
              <span className="text-[10px] text-korinth-muted">({tickers.length})</span>
            </button>
            {isOpen && (
              <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {tickers.map(r => (
                  <AssetCard
                    key={`${secName}::${r.ticker}`}
                    symbol={r.ticker} name={r.name} category={r.sector} tier={r.tier}
                    asset_type={r.asset_type || 'stock'}
                    price_usd={r.latest_price} change_24h={r.change_24h}
                    kairos_view={r.kairos_view || null}
                    themes={taxonomy[r.ticker]?.themes || []}
                    catalysts={r.recent_news || []}
                    your_position={r.holding_pct != null ? { holding_pct: r.holding_pct, target: null, note: r.personal_notes } : null}
                    onClick={() => onClick(r.ticker)}
                    show_tier_controls={true} on_tier_changed={onRefresh} on_removed={onRefresh}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TierSection({ tier, rows, onClick, onRefresh }) {
  const meta = TIER_COPY[tier];
  return (
    <ModuleContainer title={meta.label} subtitle={`${rows.length} ticker${rows.length === 1 ? '' : 's'} — ${meta.sub}`}>
      {rows.length === 0 ? (
        <div className="text-[10px] text-korinth-muted font-mono text-center py-4 italic">
          Nothing in {tier} tier yet. Use "+ Add Ticker" above.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map(r => (
            <AssetCard
              key={r.ticker}
              symbol={r.ticker}
              name={r.name}
              category={r.sector}
              tier={r.tier}
              asset_type={r.asset_type || 'stock'}
              price_usd={r.latest_price}
              change_24h={r.change_24h}
              kairos_view={r.kairos_view || null}
              themes={r.themes || []}
              catalysts={r.recent_news || []}
              your_position={r.holding_pct != null || r.entry_price != null || r.personal_notes ? {
                holding_pct: r.holding_pct, target: null, note: r.personal_notes,
              } : null}
              onClick={() => onClick(r.ticker)}
              show_tier_controls={true}
              on_tier_changed={() => onRefresh?.()}
              on_removed={() => onRefresh?.()}
            />
          ))}
        </div>
      )}
    </ModuleContainer>
  );
}

export default function WatchlistPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('watchlist'); }, []);

  // Sprint 19 state
  const [user, setUser] = useState({ core: [], active: [], monitor: [], summary: {} });
  const [recentNews, setRecentNews] = useState([]);
  const [polyDivergent, setPolyDivergent] = useState([]);
  const [detailTicker, setDetailTicker] = useState(null);

  // Sprint 13 (legacy) state
  const [hierarchy, setHierarchy] = useState([]);
  const [urgent, setUrgent] = useState([]);
  const [unusual, setUnusual] = useState([]);
  const [expandedSectors, setExpandedSectors] = useState({});
  const [showLegacy, setShowLegacy] = useState(false);

  // Sprint 21.7: view mode (tier | theme | flat) + taxonomy cache
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('watchlist_view_mode') || 'tier');
  const [taxonomyByTicker, setTaxonomyByTicker] = useState({});
  const [expandedThemes, setExpandedThemes] = useState({});

  useEffect(() => { localStorage.setItem('watchlist_view_mode', viewMode); }, [viewMode]);

  async function loadUser() {
    const json = async (url) => { try { const r = await fetch(url); return r.ok ? await r.json() : null; } catch { return null; } };
    const u = await json('/api/watchlist/user') || { core: [], active: [], monitor: [], summary: {} };

    // Enrich each ticker with LIGHTWEIGHT summary (Sprint 19.5 fix — no shadow P&L per row)
    async function enrich(rows) {
      return Promise.all(rows.map(async r => {
        const d = await json(`/api/watchlist/ticker/${r.ticker}/summary`);
        if (!d) return r;
        return {
          ...r,
          latest_price: d.latest_price,
          change_24h: d.change_24h,
          kairos_view: {
            predictions: d.top_pred ? [d.top_pred] : [],
            recommendation: d.top_rec || null,
            hit_rate_30d: null, // only in full detail, not in summary
          },
          themes: [], // theme_count only in summary — avoid n+1 theme fetch
          theme_count: d.theme_count || 0,
          recent_news: d.top_news ? [{ headline: d.top_news.headline, sentiment: d.top_news.sentiment, observed_at: d.top_news.published_at }] : [],
        };
      }));
    }

    u.core = await enrich(u.core);
    u.active = await enrich(u.active);
    u.monitor = await enrich(u.monitor);
    setUser(u);

    setRecentNews(await json('/api/watchlist/news/recent?limit=15&min_materiality=3') || []);
    setPolyDivergent(await json('/api/polymarket/divergent?limit=8') || []);

    // Sprint 21.7: fetch taxonomy for all watchlist tickers so theme grouping works
    const allTickers = [...u.core, ...u.active, ...u.monitor].map(r => r.ticker);
    const taxMap = {};
    await Promise.all(allTickers.map(async t => {
      const d = await json(`/api/taxonomy/ticker/${t}`);
      if (d) taxMap[t] = d;
    }));
    setTaxonomyByTicker(taxMap);
  }

  async function loadLegacy() {
    const json = async (url) => { try { const r = await fetch(url); return r.ok ? await r.json() : null; } catch { return null; } };
    setHierarchy(await json('/api/watchlist') || []);
    setUrgent(await json('/api/watchlist/signals/urgent?limit=5') || []);
    setUnusual(await json('/api/watchlist/mentions/unusual?threshold=3') || []);
  }

  useEffect(() => {
    loadUser();
    loadLegacy();
    const iv = setInterval(() => { loadUser(); loadLegacy(); }, 120000);
    return () => clearInterval(iv);
  }, []);

  const totalBook = (user.summary?.total_holdings_pct || 0).toFixed(1);

  return (
    <div className="space-y-6">
      {/* Header with Add button */}
      <div className="flex items-center gap-3 flex-wrap">
        <ListChecks className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Watchlist</h1>
        <span className="text-korinth-muted text-sm font-mono">
          {user.summary?.core_count || 0} core · {user.summary?.active_count || 0} active · {user.summary?.monitor_count || 0} monitor
          {totalBook > 0 ? ` · ${totalBook}% of book tracked` : ''}
        </span>
        <div className="ml-auto">
          <AddAssetButton asset_type="stock" default_tier="monitor" on_added={loadUser} label="+ Add Ticker" />
        </div>
      </div>

      {/* Explainer */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg p-3 text-[11px] font-mono text-korinth-muted leading-relaxed">
        <div className="flex items-center gap-2 mb-1">
          <Target className="w-3.5 h-3.5 text-korinth-accent" />
          <span className="text-korinth-text font-bold">Personal Intelligence Layer</span>
        </div>
        <p>
          Your curated book drives KAIROS's personal recommendations. <b className="text-korinth-accent">Core</b> tickers
          get priority in daily briefings and KAIROS will anchor theses to them when relevant.
          <b className="text-korinth-accent"> Active</b> tickers get short/swing-horizon coverage.
          <b className="text-korinth-accent"> Monitor</b> captures headlines only (no per-ticker Claude classification — light on cost).
          Promote/demote any ticker from its detail modal.
        </p>
      </div>

      {/* Stat strip: recent news + polymarket divergence */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Recent material news across the book */}
        <div className="bg-korinth-surface border border-korinth-border rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Newspaper className="w-4 h-4 text-korinth-accent" />
            <span className="text-xs font-mono font-bold text-white">Material news (24h, your book)</span>
            <span className="text-[10px] text-korinth-muted">{recentNews.length}</span>
          </div>
          {recentNews.length === 0 ? (
            <div className="text-[10px] text-korinth-muted font-mono italic">No material headlines on your tickers in 24h.</div>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {recentNews.slice(0, 8).map(n => {
                const sentColor = n.sentiment === 'bullish' ? '#10b981' : n.sentiment === 'bearish' ? '#ef4444' : '#6b7280';
                return (
                  <div key={n.id} className="flex items-start gap-2 px-2 py-1 bg-korinth-bg rounded text-[10px] font-mono">
                    <span className="text-white font-bold w-14 flex-shrink-0">{n.ticker}</span>
                    <span className="flex-shrink-0 text-[8px] px-1 py-0.5 rounded" style={{ color: sentColor, backgroundColor: sentColor + '15' }}>M{n.materiality}</span>
                    <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-korinth-text flex-1 truncate hover:text-korinth-accent">{n.headline}</a>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Polymarket divergence */}
        <div className="bg-korinth-surface border border-korinth-border rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-korinth-warning" />
            <span className="text-xs font-mono font-bold text-white">Polymarket vs KAIROS</span>
            <span className="text-[10px] text-korinth-muted">{polyDivergent.length} divergent</span>
          </div>
          {polyDivergent.length === 0 ? (
            <div className="text-[10px] text-korinth-muted font-mono italic">No significant divergence with Polymarket markets on your book.</div>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {polyDivergent.slice(0, 6).map(pm => (
                <div key={pm.id} className="flex items-start gap-2 px-2 py-1 bg-korinth-bg rounded text-[10px] font-mono">
                  <span className="text-korinth-text flex-1 truncate">{pm.question}</span>
                  <span className="text-white font-bold">{((pm.yes_price || 0) * 100).toFixed(0)}%</span>
                  <span className="text-[8px] font-bold px-1 rounded bg-korinth-warning/15 text-korinth-warning flex-shrink-0">Δ{((pm.kairos_divergence || 0) * 100).toFixed(0)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sprint 21.7: view mode toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider">View:</span>
        {['tier','theme','sector'].map(m => (
          <button key={m} onClick={() => setViewMode(m)}
            className={`text-[10px] font-mono px-3 py-1.5 rounded border ${viewMode === m ? 'bg-korinth-accent/10 border-korinth-accent text-korinth-accent font-bold' : 'border-korinth-border text-korinth-muted hover:text-korinth-text'}`}>
            {m === 'tier' ? 'Tier' : m === 'theme' ? 'Theme' : 'Sector'}
          </button>
        ))}
      </div>

      {viewMode === 'tier' && (
        <>
          <TierSection tier="core" rows={user.core} onClick={setDetailTicker} onRefresh={loadUser} />
          <TierSection tier="active" rows={user.active} onClick={setDetailTicker} onRefresh={loadUser} />
          <TierSection tier="monitor" rows={user.monitor} onClick={setDetailTicker} onRefresh={loadUser} />
        </>
      )}

      {viewMode === 'theme' && (
        <ThemeGroupedView rows={[...user.core, ...user.active, ...user.monitor]} taxonomy={taxonomyByTicker}
          expanded={expandedThemes} setExpanded={setExpandedThemes} onClick={setDetailTicker} onRefresh={loadUser} />
      )}

      {viewMode === 'sector' && (
        <SectorGroupedView rows={[...user.core, ...user.active, ...user.monitor]} taxonomy={taxonomyByTicker}
          expanded={expandedThemes} setExpanded={setExpandedThemes} onClick={setDetailTicker} onRefresh={loadUser} />
      )}

      {/* Legacy Sprint 13 view (collapsed by default) */}
      <div>
        <button
          onClick={() => setShowLegacy(v => !v)}
          className="flex items-center gap-2 text-[11px] font-mono text-korinth-muted hover:text-korinth-text"
        >
          {showLegacy ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          Legacy sector view ({urgent.length} urgent signals, {unusual.length} unusual mentions)
        </button>

        {showLegacy && (
          <div className="mt-4 space-y-4">
            {/* Urgent Signals */}
            {urgent.length > 0 && (
              <ModuleContainer title="Urgent Signals" subtitle={`${urgent.length} actionable picks (legacy sector-level)`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {urgent.map(u => (
                    <div key={u.ticker} className={`px-4 py-3 rounded-lg border-l-4 ${u.kairos_signal === 'buy' ? 'border-l-korinth-accent bg-korinth-accent/5' : 'border-l-korinth-danger bg-korinth-danger/5'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-mono font-bold text-white">{u.ticker}</span>
                          <span className="text-xs font-mono font-bold px-2 py-0.5 rounded" style={{ color: SIGNAL_COLORS[u.kairos_signal], backgroundColor: (SIGNAL_COLORS[u.kairos_signal]) + '15' }}>
                            {u.kairos_signal?.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-xl font-mono font-bold" style={{ color: SIGNAL_COLORS[u.kairos_signal] }}>{u.signal_strength}</span>
                      </div>
                      <div className="text-[11px] text-korinth-text font-mono">{u.name}</div>
                      <div className="text-[10px] text-korinth-muted font-mono">{u.sector_name} → {u.subsector_name}</div>
                      {u.signal_reasoning && <p className="text-[10px] text-korinth-text mt-1.5 leading-relaxed">{u.signal_reasoning}</p>}
                    </div>
                  ))}
                </div>
              </ModuleContainer>
            )}

            {/* Unusual mentions */}
            {unusual.length > 0 && (
              <ModuleContainer title="Unusual News Activity" subtitle="Tickers getting abnormal Telegram mention volume (24h)">
                <div className="space-y-2">
                  {unusual.map(u => (
                    <div key={u.ticker} className="flex items-center gap-3 px-3 py-2 bg-korinth-warning/5 border border-korinth-warning/20 rounded-lg">
                      <Zap className="w-4 h-4 text-korinth-warning flex-shrink-0" />
                      <div className="flex-1">
                        <span className="text-xs font-mono font-bold text-white">{u.ticker}</span>
                        <span className="text-[10px] text-korinth-muted ml-2">{u.name}</span>
                      </div>
                      <span className="text-sm font-mono font-bold text-korinth-warning">{u.mention_count_24h}x</span>
                    </div>
                  ))}
                </div>
              </ModuleContainer>
            )}

            {/* Sector hierarchy */}
            <ModuleContainer title="Sector Hierarchy" subtitle="Legacy watchlist browsed by sector">
              <div className="space-y-0.5">
                {hierarchy.map(sector => {
                  const isOpen = expandedSectors[sector.id];
                  return (
                    <div key={sector.id}>
                      <button onClick={() => setExpandedSectors(e => ({ ...e, [sector.id]: !e[sector.id] }))}
                        className="flex items-center gap-2 w-full px-2 py-2 text-left hover:bg-korinth-bg/50 rounded transition-colors">
                        {isOpen ? <ChevronDown className="w-3 h-3 text-korinth-muted" /> : <ChevronRight className="w-3 h-3 text-korinth-muted" />}
                        <span className="text-xs font-mono font-bold text-white">{sector.name}</span>
                        {sector.kairos_stance && <span className="text-[7px] font-mono font-bold uppercase px-1 py-0.5 rounded" style={{ color: STANCE_COLORS[sector.kairos_stance], backgroundColor: (STANCE_COLORS[sector.kairos_stance]) + '10' }}>{sector.kairos_stance}</span>}
                        <span className="text-[9px] text-korinth-muted ml-auto">{sector.subsectors.reduce((n, s) => n + s.tickers.length, 0)}</span>
                      </button>
                      {isOpen && sector.subsectors.map(sub => (
                        <div key={sub.id} className="ml-3">
                          <div className="flex items-center gap-2 px-2 py-1">
                            <span className="text-[10px] font-mono text-korinth-accent uppercase tracking-wider">{sub.name}</span>
                          </div>
                          <div className="ml-2 mb-2 space-y-0.5">
                            {sub.tickers.map(t => (
                              <button key={t.ticker} onClick={() => setDetailTicker(t.ticker)}
                                className="flex items-center gap-2 w-full px-2 py-1 text-left bg-korinth-bg rounded text-[10px] font-mono hover:bg-korinth-border/30 transition-colors">
                                <span className="text-white font-bold w-12">{t.ticker}</span>
                                <span className="text-korinth-text flex-1 truncate">{t.name}</span>
                                {t.kairos_signal && <span className="font-bold px-1 rounded" style={{ color: SIGNAL_COLORS[t.kairos_signal], backgroundColor: (SIGNAL_COLORS[t.kairos_signal]) + '10' }}>{t.kairos_signal} {t.signal_strength}</span>}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </ModuleContainer>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detailTicker && (
        <AssetDetailModal
          symbol={detailTicker}
          asset_type="stock"
          on_close={() => setDetailTicker(null)}
          on_action={loadUser}
        />
      )}
    </div>
  );
}
